import { BoxType, type CustomTemplateBox } from './custom-template';

type RefParams = {
  clipBaseBoxId?: string;
  sourceGroupId?: string;
  maskBaseBoxId?: string;
  compoundClipBakeRecipe?: { leafBoxIds?: readonly string[]; sourceTextBoxIds?: readonly string[] };
};

/**
 * Every box-to-box reference a box carries (clip base, mask base, clip recipe leafs, source group), re-pointed
 * through `ids`. A re-mint of box ids without it leaves the references on the old ids, and the renderer refuses
 * a clipped adjustment whose base it cannot find. An id the map does not name stays as is.
 */
export function remapBoxReferences<T extends CustomTemplateBox>(boxes: readonly T[], ids: ReadonlyMap<string, string>): T[] {
  return boxes.map(box => remapBox(box, ids));
}

/** Ids the boxes point at (clip base, mask base, clip recipe leafs, source group), at any depth. */
export function referencedBoxIds(boxes: readonly CustomTemplateBox[]): Set<string> {
  const out = new Set<string>();
  const walk = (list: readonly CustomTemplateBox[]): void => {
    for (const box of list) {
      const p = (box as { params?: RefParams }).params;
      const recipe = p?.compoundClipBakeRecipe;
      for (const id of [
        p?.clipBaseBoxId,
        p?.sourceGroupId,
        p?.maskBaseBoxId,
        ...(recipe?.leafBoxIds ?? []),
        ...(recipe?.sourceTextBoxIds ?? []),
      ])
        if (id) out.add(id);
      if (box.type === BoxType.GROUP && 'boxes' in box && box.boxes) walk(box.boxes);
    }
  };

  walk(boxes);
  return out;
}

function remapBox<T extends CustomTemplateBox>(box: T, ids: ReadonlyMap<string, string>): T {
  let next = box;
  if (box.type === BoxType.GROUP && 'boxes' in box && box.boxes) {
    const inner = remapBoxReferences(box.boxes, ids);
    if (inner.some((b, i) => b !== box.boxes[i])) next = { ...next, boxes: inner };
  }

  const params = (box as { params?: RefParams }).params;
  if (!params) return next;
  const to = (id: string | undefined): string | undefined => (id ? ids.get(id) : undefined);
  const recipe = params.compoundClipBakeRecipe;
  const patch: RefParams = {
    ...(to(params.clipBaseBoxId) && { clipBaseBoxId: to(params.clipBaseBoxId) }),
    ...(to(params.sourceGroupId) && { sourceGroupId: to(params.sourceGroupId) }),
    ...(to(params.maskBaseBoxId) && { maskBaseBoxId: to(params.maskBaseBoxId) }),
    ...(recipe &&
      [...(recipe.leafBoxIds ?? []), ...(recipe.sourceTextBoxIds ?? [])].some(id => ids.has(id)) && {
        compoundClipBakeRecipe: {
          ...recipe,
          leafBoxIds: (recipe.leafBoxIds ?? []).map(id => ids.get(id) ?? id),
          sourceTextBoxIds: (recipe.sourceTextBoxIds ?? []).map(id => ids.get(id) ?? id),
        },
      }),
  };
  return Object.keys(patch).length ? ({ ...next, params: { ...params, ...patch } } as T) : next;
}
